// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimeUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { DATE_TIME_FORMAT } from 'logic/data/Izenda.TimeFormatConstants';
import uiState from 'logic/Izenda.UiState';
import { get, filter, isNil, includes, getOr, reverse, isEmpty, reduce } from 'lodash/fp';
import moment from 'moment';
import AppGlobal from 'Izenda.AppGlobal';
import { MinDate, MaxDate } from 'config';
import {
  DATE_TIME_FORMAT as DATE_TIME_FORMAT_PARSE,
  TIME_FORMAT,
  DATE_FORMAT,
  MONTHS,
  DEFAULT_TIME_VALUE
} from 'logic/data/Izenda.AppGlobalConstants';

export default {
  TimeZoneOffset: {
    // pattern for check when typing offset in input
    onChangePattern: /^(\+|\-)?((1[0-2])|0?[0-9])?$/,
    // pattern for check when finish typing, need to have number
    finishedPattern: /^(\+|\-)?((1[0-2])|0?[0-9])$/,
    // check value match with typing pattern
    checkTimeZoneFormatOnTyping: function (value) {
      return this.onChangePattern.test(value);
    },
    // check value match with finished pattern
    checkTimeZoneFormat: function (value) {
      return this.finishedPattern.test(value);
    },
    // format number to have plus sign if possitive
    formatTimeZone: function (value) {
      const prefix = value > 0 ? '+' : '';
      return prefix + value;
    }
  },

  DEFAULT_TIME_VALUE:DEFAULT_TIME_VALUE,

  /**
   * Get list of support date
   * @param {any} format date time format
   */
  filterDateTimeFormat: filter(function (format: any) {
    const listSupportedDateSetting = get('FOR_DATE_SETTING', DATE_TIME_FORMAT[format.name]);
    return isNil(listSupportedDateSetting) || includes(uiState.currentUser.dateFormat, listSupportedDateSetting);
  }),

  /**
   * compare date string Format
   * @param {any} str1 first date string
   * @param {any} str2 second date string
   */
  compareDateStringFormat: function (str1: any, str2: any) {
    const date1 = new Date(str1);
    const date2 = new Date(str2);

    return date1.getTime() > date2.getTime();
  },

  /**
   * Get current date time base on UTC time and time stamp offset
   */
  getUtcTimeWithTimestampOffset: function () {
    let currentDate = moment.utc();
    const defaultTimestampOffset = getOr(0, 'advancedSettings.other.timezoneForTimestampOffsetValue', AppGlobal);
    const userTimestampOffset = get('currentUser.timestampOffset', uiState);
    const timestampOffset = !isNil(userTimestampOffset) ? userTimestampOffset : defaultTimestampOffset;
    currentDate.add(timestampOffset, 'hours');
    return currentDate;
  },

  /**
   * Reverse range value if needed
   * @param {Array} data
   */
  reverseRangeValuesIfNeeded: function (data: Array) {
    return data[0] && data[0].isAfter(data[1]) ? reverse(data) : data;
  },

  /**
   * Resolve specified min and max date time
   */
  getConfiguredMinMaxDateTime: function () {
    return {
      MinDate: moment(MinDate)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toDate(),
      MaxDate: moment(MaxDate)
        .set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
        .toDate()
    };
  },

  /**
   * Get now without times
   */
  getNowWithoutTime: function () {
    return moment({ h: 0, m: 0, s: 0, ms: 0 });
  },

  /**
   * parse date time
   * @param {string} input
   * @param {Array<string>} additionalFormats
   */
  parseDateTime: function (input: string, additionalFormats: Array<string> = []) {
    if (isEmpty(input)) return null;
    let value = null;


    const formats = [DATE_TIME_FORMAT_PARSE, DATE_FORMAT, TIME_FORMAT].concat(additionalFormats);
    const tryParseFormats = () => {
      return reduce(
        (parseResult, format) => {
          // Ignore if the input had been parsed successfully before
          if (parseResult) return true;
          const test = moment(input, format, true);
          test.isValid() && (value = test);
          return test.isValid();
        },
        false,
        formats
      );
    };
    const result = tryParseFormats();

    return result ? value : null;
  },

  /**
   * Check if a date time is valid
   * @param {moment} dateTime
   */
  formattedDateTime: function (dateTime) {
    const formattedDate = new Date(dateTime);
    const nth = function (d) {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
    const day = `${formattedDate.getDate()}${nth(formattedDate.getDate())}`;
    const monthName = MONTHS[formattedDate.getMonth()];
    const year = formattedDate.getFullYear();
    const newDate = `${monthName} ${day}, ${year}`;
    return newDate;
  },

  /**
   * format date
   * @param {moment} dateTime
   */
  isDateTimeValid: function (dateTime: moment) {
    return dateTime && dateTime.isValid();
  },

  isDateValid: function(value, format) {
    format = format.split(' ')[0];
    var date = moment(value, format);
    if (date == null || !date.isValid()) return false;
    return value.indexOf(date.format(format)) >= 0;
  }
};



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.DateTimeUtils.js